import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import { useLang } from "@/context/LangContext";
import { ArrowLeft, Calendar as Cal, User, Languages, Download, FileText, Lock, Link2, QrCode, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;
const DATE_LOCALES = { fr: "fr-FR", en: "en-US", es: "es-ES", it: "it-IT", pt: "pt-PT" };


export default function NewsDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [n, setN] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/news/${slug}`, { params: { lang } }).then((r) => {
      setN(r.data);
      return api.get("/news", { params: { lang } }).then((rl) => setRelated(rl.data.filter((x) => x.slug !== slug && x.category === r.data.category).slice(0, 3)));
    }).catch(() => {});
  }, [slug, lang]);

  if (!n) return <PublicLayout><div className="py-20 text-center text-neutral-500">{t("loading") || "Chargement..."}</div></PublicLayout>;

  // Detect publication (has a PDF attached) → render dedicated landing layout
  const isPublication = Boolean(n.pdf_url || n.pdf_filename);

  if (isPublication) return <PublicationLanding n={n} t={t} lang={lang} related={related} />;

  // Regular news article layout (unchanged)
  return (
    <PublicLayout>
      <Seo
        title={n.title}
        description={n.excerpt?.slice(0, 160)}
        path={`/news/${n.slug}`}
        image={n.image ? resolveUrl(n.image) : undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: n.title,
          description: n.excerpt,
          image: n.image ? resolveUrl(n.image) : undefined,
          datePublished: n.published_at,
          author: { "@type": "Person", name: n.author },
          publisher: { "@type": "Organization", name: "La WineTech", logo: { "@type": "ImageObject", url: "https://lawinetech.com/logo512.png" } },
          articleSection: n.category,
          keywords: (n.tags || []).join(", "),
        }}
      />
      <article className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/news" className="inline-flex items-center gap-1.5 text-sm font-medium text-wine-600 hover:text-wine-700" data-testid="back-to-news">
          <ArrowLeft className="h-4 w-4" /> {t("back_to_news")}
        </Link>
        <span className="mt-6 inline-block rounded-full bg-wine-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-wine-700">{n.category}</span>
        <h1 className="mt-4 text-4xl font-bold leading-tight text-navy md:text-5xl" data-testid="news-detail-title">{n.title}</h1>
        {n._translated && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs text-amber-700" data-testid="auto-translated-badge">
            <Languages className="h-3 w-3" />
            {t("auto_translated") || "Traduit automatiquement"}
          </div>
        )}
        <div className="mt-5 flex items-center gap-4 text-sm text-neutral-500">
          <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {n.author}</span>
          <span className="inline-flex items-center gap-1.5"><Cal className="h-3.5 w-3.5" /> {n.published_at && new Date(n.published_at).toLocaleDateString(DATE_LOCALES[lang] || "fr-FR", { dateStyle: "long" })}</span>
        </div>
        {n.image && <img src={resolveUrl(n.image)} alt={n.title} className="mt-8 aspect-[16/9] w-full rounded-2xl object-cover" />}
        <div className="prose prose-neutral mt-8 max-w-none text-lg leading-relaxed text-neutral-800">
          <p className="text-xl text-neutral-700">{n.excerpt}</p>
          {/* Sprint 91 — render markdown so #, ##, **, [](), - render as HTML on the public page (was showing raw chars). */}
          <div className="mt-6" dangerouslySetInnerHTML={{ __html: renderMarkdown(n.body) }} data-testid="news-body-rendered" />
        </div>
        {n.tags?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {n.tags.map((tag) => <span key={tag} className="rounded-full bg-wine-50/60 px-2.5 py-1 text-xs text-wine-700">#{tag}</span>)}
          </div>
        )}
        {related.length > 0 && (
          <div className="mt-16">
            <h3 className="font-serif text-2xl text-neutral-900">{t("related_articles")}</h3>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to={`/news/${r.slug}`} className="block rounded-xl border border-neutral-100 bg-white p-4 hover:bg-wine-50/30" data-testid={`related-news-${r.id}`}>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-wine-600">{r.category}</span>
                  <h4 className="mt-1.5 font-serif text-base text-neutral-900">{r.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </PublicLayout>
  );
}


// =====================================================================
// Publication landing page — branded HTML presentation of a white paper
// =====================================================================
function PublicationLanding({ n, t, lang, related }) {
  const [qrOpen, setQrOpen] = useState(false);
  const [rgpdOpen, setRgpdOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", company: "", phone: "", job_title: "", consent_rgpd: false });
  const [submitting, setSubmitting] = useState(false);

  const stablePdfUrl = `${BACKEND}/api/news/${n.slug}/pdf`;
  const landingUrl = typeof window !== "undefined" ? `${window.location.origin}/news/${n.slug}` : "";
  const coverSrc = n.pdf_thumbnail ? resolveUrl(n.pdf_thumbnail) : (n.image ? resolveUrl(n.image) : null);

  const triggerDownload = () => {
    if (n.lead_capture_enabled) { setRgpdOpen(true); return; }
    window.open(stablePdfUrl, "_blank");
  };

  const copyLanding = () => {
    navigator.clipboard.writeText(landingUrl).then(
      () => toast.success("Lien de la page copié"),
      () => toast.error("Impossible de copier le lien"),
    );
  };

  const submitRgpd = async (e) => {
    e.preventDefault();
    if (!form.consent_rgpd) return toast.error("Merci d'accepter la politique RGPD");
    setSubmitting(true);
    try {
      const res = await api.post(`/news/${n.slug}/request-download`, form);
      if (res.data?.pdf_url) {
        window.open(resolveUrl(res.data.pdf_url), "_blank");
      } else {
        window.open(stablePdfUrl, "_blank");
      }
      setRgpdOpen(false);
      toast.success("Téléchargement déclenché");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <Seo
        title={`${n.title} — Livre blanc`}
        description={n.excerpt?.slice(0, 160)}
        path={`/news/${n.slug}`}
        image={coverSrc || undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Book",
          name: n.title,
          description: n.excerpt,
          image: coverSrc || undefined,
          inLanguage: lang || "fr",
          publisher: { "@type": "Organization", name: "La WineTech" },
          datePublished: n.published_at,
          url: landingUrl,
        }}
      />

      {/* Hero — dark immersive */}
      <section className="relative overflow-hidden bg-gradient-to-br from-navy via-[#1a1f3a] to-[#0e1228] text-white">
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-wine-500/20 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-wine-700/30 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-6 py-16 lg:py-24">
          <Link to="/news" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-wine-300 hover:text-wine-200" data-testid="back-to-news">
            <ArrowLeft className="h-3.5 w-3.5" /> Retour aux publications
          </Link>

          <div className="mt-8 grid gap-12 lg:grid-cols-[320px_1fr] lg:gap-16">
            {/* Cover */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              {coverSrc ? (
                <img
                  src={coverSrc}
                  alt={`Couverture ${n.title}`}
                  className="w-full max-w-[320px] rounded-xl border border-white/10 shadow-2xl shadow-black/50 ring-1 ring-white/10"
                  data-testid="publication-cover"
                />
              ) : (
                <div className="aspect-[3/4] w-full max-w-[320px] grid place-items-center rounded-xl border border-white/10 bg-white/5 text-white/40">
                  <FileText className="h-20 w-20" />
                </div>
              )}
              <div className="mt-4 flex items-center gap-3 text-xs text-white/60">
                <Download className="h-3.5 w-3.5 text-wine-300" />
                <span>{n.download_count || 0} téléchargement{(n.download_count || 0) > 1 ? "s" : ""}</span>
                {n.lead_capture_enabled && (
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-200"><Lock className="h-2.5 w-2.5" />RGPD</span>
                )}
              </div>
            </div>

            {/* Content */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-wine-300">Publication / {n.category || "Livre blanc"}</div>
              <h1 className="mt-4 font-serif text-4xl leading-tight md:text-5xl" data-testid="publication-title">{n.title}</h1>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/60">
                <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {n.author}</span>
                {n.published_at && <span className="inline-flex items-center gap-1.5"><Cal className="h-3.5 w-3.5" /> {new Date(n.published_at).toLocaleDateString(DATE_LOCALES[lang] || "fr-FR", { dateStyle: "long" })}</span>}
              </div>

              <div className="mt-10">
                <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-wine-300">Présentation</div>
                <p className="mt-3 whitespace-pre-line text-lg leading-relaxed text-white/90" data-testid="publication-excerpt">{n.excerpt}</p>
              </div>

              {n.body && (
                <div className="mt-10">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-wine-300">Description détaillée</div>
                  <div className="mt-3 text-base leading-relaxed text-white/80" data-testid="publication-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(n.body) }} />
                </div>
              )}

              {n.tags?.length > 0 && (
                <div className="mt-10 flex flex-wrap gap-2">
                  {n.tags.map((tag) => (
                    <span key={tag} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-wine-200">#{tag}</span>
                  ))}
                </div>
              )}

              {/* CTA bar */}
              <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-white/10 pt-8">
                <button
                  onClick={triggerDownload}
                  className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-7 py-3 text-sm font-bold text-white shadow-lg shadow-wine-500/30 transition-all hover:bg-wine-600 hover:shadow-wine-500/50"
                  data-testid="publication-download-btn"
                >
                  <Download className="h-4 w-4" /> Télécharger le PDF
                </button>
                <button
                  onClick={copyLanding}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur-md hover:bg-white/10"
                  data-testid="publication-copy-link-btn"
                  title="Copier l'adresse de cette page"
                >
                  <Link2 className="h-4 w-4" /> Copier le lien de la page
                </button>
                <button
                  onClick={() => setQrOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur-md hover:bg-white/10"
                  data-testid="publication-qr-btn"
                >
                  <QrCode className="h-4 w-4" /> QR Code
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16">
          <h3 className="font-serif text-2xl text-neutral-900">À découvrir aussi</h3>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.id} to={`/news/${r.slug}`} className="block rounded-xl border border-neutral-100 bg-white p-4 hover:bg-wine-50/30" data-testid={`related-news-${r.id}`}>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-wine-600">{r.category}</span>
                <h4 className="mt-1.5 font-serif text-base text-neutral-900">{r.title}</h4>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* QR modal */}
      {qrOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4" onClick={() => setQrOpen(false)} data-testid="publication-qr-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-wine-600">QR Code</div>
                <h3 className="mt-1 text-lg font-bold text-navy">{n.title}</h3>
              </div>
              <button onClick={() => setQrOpen(false)} className="rounded-full p-1 hover:bg-neutral-100" data-testid="close-qr-modal"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid place-items-center rounded-2xl border-2 border-wine-100 bg-wine-50/40 p-6">
              <img src={`${BACKEND}/api/news/${n.slug}/qr?size=10`} alt={`QR ${n.title}`} className="h-64 w-64" data-testid="qr-image" />
            </div>
            <p className="mt-3 text-center text-xs text-neutral-600">Idéal pour salons, kakémonos, badges. Scan → page du livre blanc.</p>
          </div>
        </div>
      )}

      {/* RGPD form */}
      {rgpdOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => setRgpdOpen(false)} data-testid="publication-rgpd-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-wine-600">Accès au livre blanc</div>
                <h3 className="mt-1 text-xl font-bold text-navy">Renseignez vos coordonnées</h3>
              </div>
              <button onClick={() => setRgpdOpen(false)} className="rounded-full p-1 hover:bg-neutral-100" data-testid="close-rgpd"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submitRgpd} className="mt-5 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <input required placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="rgpd-first-name" />
                <input required placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="rgpd-last-name" />
              </div>
              <input required type="email" placeholder="Email professionnel" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="rgpd-email" />
              <div className="grid gap-3 sm:grid-cols-2">
                <input placeholder="Société" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="rgpd-company" />
                <input placeholder="Fonction" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="rgpd-job" />
              </div>
              <label className="flex items-start gap-2 text-xs text-neutral-700">
                <input type="checkbox" required checked={form.consent_rgpd} onChange={(e) => setForm({ ...form, consent_rgpd: e.target.checked })} className="mt-0.5 h-4 w-4" data-testid="rgpd-consent" />
                J&apos;accepte que La WineTech traite mes données conformément à sa politique de confidentialité.
              </label>
              <button type="submit" disabled={submitting} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="rgpd-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Télécharger maintenant
              </button>
            </form>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}
